import React, { useState, useEffect, useCallback } from "react";
import { usePageMetadata } from "../context/PageMetadataContext";
import { useLanguage } from "../context/LanguageContext";
import { Table, Spinner, Form, Button, Row, Col, Alert } from "react-bootstrap";
import { Card, CardContent } from "../components/Card";
import AccountLabel from "../components/AccountLabel";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { PiscanAPI } from "../api/piscan"; // Assuming PiscanAPI is defined here
import { timelib } from "../utils/time"; // Import timelib

// Interfaces for API response
interface OperationDetails {
  to: string;
  from: string;
  amount: string;
  asset_type: string;
}

interface Operation {
  details: OperationDetails;
  source_account: string;
  transaction_hash: string;
  transaction_time: string;
}

interface TransactionsApiResponse {
  operations: Operation[];
  pagination: {
    page: number;
    limit: number;
    has_next: boolean;
  };
}

interface Filters {
  address: string;
  minAmount: string;
  maxAmount: string;
}

const PAGE_LIMIT = 20;

const TransactionsFilter: React.FC = () => {
  const { t, language } = useLanguage();
  const { setHeading, setTitle, setDescription } = usePageMetadata();
  const location = useLocation();
  const navigate = useNavigate();

  const [transactions, setTransactions] = useState<Operation[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>({
    address: "",
    minAmount: "10000", // Default minAmount
    maxAmount: "",
  });
  const [amountError, setAmountError] = useState<string | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<Filters | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);

  useEffect(() => {
    setHeading(t("transactions_filter.heading", { defaultValue: "Filter Transactions" }));
    setTitle(t("transactions_filter.title", { defaultValue: "Filter Transactions | Pi Scan" }));
    setDescription(
      t(
        "transactions_filter.description",
        { defaultValue: "Filter and search for Pi Network transactions based on address, amount, and other criteria." }
      )
    );
  }, [setHeading, setTitle, setDescription, t, language]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const addressFromUrl = params.get("address");
    const minAmountFromUrl = params.get("minAmount");
    const maxAmountFromUrl = params.get("maxAmount");

    const initialFiltersState: Filters = {
      address: addressFromUrl || "",
      minAmount: minAmountFromUrl || "10000",
      maxAmount: maxAmountFromUrl || "",
    };
    setFilters(initialFiltersState);

    if (addressFromUrl && addressFromUrl.trim() !== "") {
      let isValidForAutoApply = true;
      let tempAmountError: string | null = null;

      if (initialFiltersState.minAmount && parseFloat(initialFiltersState.minAmount) < 10000) {
        tempAmountError = t("transactions_filter.amount_too_low_error", { defaultValue: "Minimum amount cannot be less than 10,000" });
        isValidForAutoApply = false;
      }
      if (initialFiltersState.maxAmount && parseFloat(initialFiltersState.maxAmount) < 10000) {
        tempAmountError = tempAmountError || t("transactions_filter.amount_too_low_error_max", { defaultValue: "Maximum amount cannot be less than 10,000" });
        isValidForAutoApply = false;
      }
      if (initialFiltersState.minAmount && initialFiltersState.maxAmount &&
          parseFloat(initialFiltersState.minAmount) > parseFloat(initialFiltersState.maxAmount)) {
        tempAmountError = tempAmountError || t("transactions_filter.min_exceeds_max_error", { defaultValue: "Minimum amount cannot exceed maximum amount" });
        isValidForAutoApply = false;
      }

      setAmountError(tempAmountError);

      if (isValidForAutoApply) {
        setAppliedFilters(initialFiltersState);
        setCurrentPage(1);
      } else {
        setAppliedFilters(null);
      }
    } else if (addressFromUrl && addressFromUrl.trim() === "") {
      setAddressError(t("transactions_filter.address_required_error", { defaultValue: "Address is required." }));
      setAppliedFilters(null);
    } else if (!addressFromUrl && !minAmountFromUrl && !maxAmountFromUrl) {
      setAppliedFilters(null);
    }
  }, [location.search, t]);

  const fetchTransactions = useCallback(
    async (page: number, currentFilters: Filters) => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (currentFilters.address) params.append("address", currentFilters.address);
        if (currentFilters.minAmount) params.append("min_amount", currentFilters.minAmount);
        if (currentFilters.maxAmount) params.append("max_amount", currentFilters.maxAmount);
        params.append("limit", String(PAGE_LIMIT));
        params.append("page", String(page));

        const response = await fetch(`${PiscanAPI.Transactions}?${params.toString()}`);
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: "Network response was not ok" }));
          throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }
        const data: TransactionsApiResponse = await response.json();
        setTransactions(data.operations || []);
        setCurrentPage(data.pagination.page);
        setHasNextPage(data.pagination.has_next);
      } catch (err) {
        console.error("Error fetching transactions:", err);
        setError(err instanceof Error ? err.message : String(err));
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (appliedFilters) {
      fetchTransactions(currentPage, appliedFilters);
    }
  }, [currentPage, appliedFilters, fetchTransactions]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === "address") {
      setAddressError(null);
    }

    if (name === "minAmount" || name === "maxAmount") {
      setAmountError(null);
      if (value && parseFloat(value) < 0) {
        processedValue = "0";
      }
    }

    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: processedValue,
    }));
  };

  const handleApplyFilters = (e?: React.FormEvent<HTMLFormElement>) => {
    if (e) e.preventDefault();

    let currentAddressError: string | null = null;
    let currentAmountError: string | null = null;
    let isValid = true;

    if (!filters.address.trim()) {
      currentAddressError = t("transactions_filter.address_required_error", { defaultValue: "Address is required." });
      isValid = false;
    }

    if (!filters.minAmount.trim()) {
      currentAmountError = t("transactions_filter.min_amount_required_error", { defaultValue: "Minimum amount is required." });
      isValid = false;
    } else if (parseFloat(filters.minAmount) < 10000) {
      currentAmountError = t("transactions_filter.amount_too_low_error", { defaultValue: "Minimum amount cannot be less than 10,000" });
      isValid = false;
    }

    if (filters.maxAmount.trim() !== "") {
      if (parseFloat(filters.maxAmount) < 10000) {
        currentAmountError = currentAmountError || t("transactions_filter.amount_too_low_error_max", { defaultValue: "Maximum amount cannot be less than 10,000" });
        isValid = false;
      }
      if (filters.minAmount.trim() && !currentAmountError && parseFloat(filters.minAmount) > parseFloat(filters.maxAmount)) {
        currentAmountError = currentAmountError || t("transactions_filter.min_exceeds_max_error", { defaultValue: "Minimum amount cannot exceed maximum amount" });
        isValid = false;
      }
    }
    
    setAddressError(currentAddressError);
    setAmountError(currentAmountError);

    if (!isValid) {
      return;
    }

    setCurrentPage(1);
    setAppliedFilters(filters);

    const newParams = new URLSearchParams();
    if (filters.address) newParams.append("address", filters.address);
    if (filters.minAmount) newParams.append("minAmount", filters.minAmount);
    if (filters.maxAmount) newParams.append("maxAmount", filters.maxAmount);
    
    if (Array.from(newParams.values()).some(val => val && val.trim() !== "")) {
      navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
    } else {
      navigate(location.pathname, { replace: true });
    }
  };

  const handleClearFilters = () => {
    const defaultFiltersState = { address: "", minAmount: "10000", maxAmount: "" };
    setFilters(defaultFiltersState);
    setAppliedFilters(null);
    setTransactions([]);
    setCurrentPage(1);
    setHasNextPage(false);
    setError(null);
    setAmountError(null);
    setAddressError(null);
    navigate(location.pathname, { replace: true });
  };

  const formatAmount = (amountStr: string): string => {
    const amount = parseFloat(amountStr);
    return amount.toLocaleString(language, {
      minimumFractionDigits: 0, // Keep minimum as 0 or 2, depending on preference
      maximumFractionDigits: 2, // Change to 2
    });
  };

  return (
    <div className="transactions-filter-page">
      <Card className="mb-4">
        <CardContent>
          <h5 className="card-title">{t("transactions_filter.filter_options", { defaultValue: "Filter Options" })}</h5>
          <Form onSubmit={handleApplyFilters}>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3" controlId="filterAddress">
                  <Form.Label>{t("transactions_filter.address", { defaultValue: "Address" })}</Form.Label>
                  <Form.Control
                    type="text"
                    name="address"
                    value={filters.address}
                    onChange={handleFilterChange}
                    placeholder={t("transactions_filter.address_placeholder", { defaultValue: "Enter account address" })}
                    isInvalid={!!addressError}
                  />
                  <Form.Control.Feedback type="invalid">
                    {addressError}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3" controlId="filterMinAmount">
                  <Form.Label>{t("transactions_filter.min_amount", { defaultValue: "Min Amount (Pi)" })}</Form.Label>
                  <Form.Control
                    type="number"
                    name="minAmount"
                    value={filters.minAmount}
                    onChange={handleFilterChange}
                    placeholder={t("transactions_filter.min_amount_placeholder", { defaultValue: "e.g., 10000" })}
                    step="any"
                    min="0"
                    isInvalid={
                      (!!amountError && (amountError.includes("10,000") || amountError.includes("required") || amountError.includes("exceed"))) ||
                      (amountError !== null &&
                        ((filters.minAmount !== "" && parseFloat(filters.minAmount) < 10000) ||
                          (filters.minAmount !== "" && filters.maxAmount !== "" && parseFloat(filters.minAmount) > parseFloat(filters.maxAmount)))
                      )
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {amountError}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3" controlId="filterMaxAmount">
                  <Form.Label>{t("transactions_filter.max_amount", { defaultValue: "Max Amount (Pi)" })}</Form.Label>
                  <Form.Control
                    type="number"
                    name="maxAmount"
                    value={filters.maxAmount}
                    onChange={handleFilterChange}
                    placeholder={t("transactions_filter.max_amount_placeholder", { defaultValue: "e.g., 100000" })}
                    step="any"
                    min="0"
                    isInvalid={
                      (!!amountError && (amountError.includes("10,000") || amountError.includes("exceed"))) || 
                      (amountError !== null &&
                        ((filters.maxAmount !== "" && parseFloat(filters.maxAmount) < 10000) ||
                          (filters.minAmount !== "" && filters.maxAmount !== "" && parseFloat(filters.minAmount) > parseFloat(filters.maxAmount)))
                      )
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {amountError}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={2} className="d-flex align-items-end mb-3">
                <Button variant="primary" type="submit" className="w-100 me-2">
                  {t("transactions_filter.apply_filters", { defaultValue: "Apply" })}
                </Button>
                <Button variant="outline-secondary" onClick={handleClearFilters} className="w-100">
                  {t("transactions_filter.clear", { defaultValue: "Clear" })}
                </Button>
              </Col>
            </Row>
          </Form>
        </CardContent>
      </Card>

      {loading && (
        <div className="d-flex justify-content-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t("common.loading", { defaultValue: "Loading..." })}</span>
          </Spinner>
        </div>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && !error && appliedFilters && transactions.length === 0 && (
        <Alert variant="info">{t("transactions_filter.no_results", { defaultValue: "No transactions found matching your criteria." })}</Alert>
      )}
      
      {!loading && !error && transactions.length > 0 && (
        <Card>
          <CardContent>
            <h5 className="card-title">{t("transactions_filter.results", { defaultValue: "Results" })}</h5>
            <div className="table-responsive">
              <Table hover className="mt-3 mb-0">
                <thead>
                  <tr>
                    <th>{t("transactions_filter.table.time", { defaultValue: "Time" })}</th>
                    <th>{t("transactions_filter.table.from", { defaultValue: "From" })}</th>
                    <th>{t("transactions_filter.table.to", { defaultValue: "To" })}</th>
                    <th className="text-end">{t("transactions_filter.table.amount", { defaultValue: "Amount (Pi)" })}</th>
                    <th>{t("transactions_filter.table.hash", { defaultValue: "Tx Hash" })}</th> 
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((op) => (
                    <tr key={op.transaction_hash + op.details.amount + op.details.to}>
                      <td>{timelib.timeAgo(op.transaction_time, language)}</td>
                      <td>
                        <AccountLabel account={op.details.from} shorten={true} />
                      </td>
                      <td>
                        <AccountLabel account={op.details.to} shorten={true} />
                      </td>
                      <td className="text-end fw-bold">
                        {formatAmount(op.details.amount)}
                      </td>
                      <td>
                        <Link to={`/tx/${op.transaction_hash}`} style={{ textDecoration: 'none' }}>
                          {op.transaction_hash.substring(0, 10)}...
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <div className="d-flex justify-content-center mt-4">
              <Button
                variant="outline-primary"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="me-2"
              >
                {t("pagination.previous", { defaultValue: "Previous" })}
              </Button>
              <span className="align-self-center">
                {t("pagination.page", { defaultValue: "Page" })} {currentPage}
              </span>
              <Button
                variant="outline-primary"
                onClick={() => setCurrentPage((prev) => prev + 1)}
                disabled={!hasNextPage}
                className="ms-2"
              >
                {t("pagination.next", { defaultValue: "Next" })}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      {!loading && !error && !appliedFilters && (
        <Alert variant="secondary">{t("transactions_filter.apply_filters_prompt", { defaultValue: "Please apply filters to see results." })}</Alert>
      )}
    </div>
  );
};

export default TransactionsFilter;
